/**
 * Instance axios singleton avec interceptors.
 *
 * Phase 3 — Services API — Pattern Factory
 *
 * AVANT : Api.js recréait une instance axios à chaque appel (api()).
 * APRÈS : Une seule instance est créée et réutilisée partout.
 *
 * Les interceptors (auth, réponse, erreurs) sont configurés UNE SEULE FOIS.
 */
import axios from 'axios'
import store from '@/store/index'
import { useMsal } from '@/msal/useMsal'
import { loginRequest } from '@/config/msal'
import { notify } from '@/composables/useNotifications'
import logger from '@/utils/logger'

// ──────────────────────────────────────────────
// Configuration
// ──────────────────────────────────────────────

const SENSITIVE_ROUTES = [
  'facturation/payerFactureManuelleCard',
  'globalpayments/card',
  'paysafe/profile/card',
]

const ERROR_EMAILS = process.env.VUE_APP_ERROR_EMAILS
  ? process.env.VUE_APP_ERROR_EMAILS.split(',')
  : []

const errorLanguage = {
  'The request does not exist': {
    fr: 'La requête n\'existe pas',
    en: 'The request does not exist',
  },
}

// ──────────────────────────────────────────────
// MSAL token helper
// ──────────────────────────────────────────────

const fetchMsalAccessToken = async () => {
  const { instance } = useMsal()
  const accounts = instance.getAllAccounts()
  try {
    const token = await instance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      accounts,
    })
    return token.accessToken
  } catch (error) {
    return instance
      .acquireTokenRedirect(loginRequest)
      .then((resp) => resp.accessToken)
      .catch((err) => {
        logger.error('MSAL acquireTokenRedirect error', err)
        return null
      })
  }
}

// ──────────────────────────────────────────────
// Auth helper
// ──────────────────────────────────────────────

const getAuthorization = async () => {
  if (process.env.VUE_APP_AUTHENTIFICATION === 'msal') return fetchMsalAccessToken()
  if (process.env.VUE_APP_AUTHENTIFICATION === 'oidc') return `Bearer ${store.state.oidcStore.access_token}`
  return null
}

// ──────────────────────────────────────────────
// Router (lazy import pour éviter les dépendances circulaires)
// ──────────────────────────────────────────────

let _router = null
const getRouter = () => {
  if (!_router) {
    _router = require('@/router').default
  }
  return _router
}

// ──────────────────────────────────────────────
// Instance axios singleton
// ──────────────────────────────────────────────

const apiClient = axios.create({
  baseURL: `${process.env.VUE_APP_SERVER_ADDRESS}${process.env.VUE_APP_SERVER_ADDRESS_PATH}`,
})

// ── Request interceptor ──────────────────────
apiClient.interceptors.request.use(
  async (config) => {
    const authorization = await getAuthorization()

    if (!authorization) {
      throw new axios.Cancel('Request canceled')
    }

    config.headers.Authorization = authorization
    config.headers['Content-Language'] = localStorage.getItem('APP_LANG_KEY')
    return config
  },
  (error) => Promise.reject(error),
)

// ── Response interceptor ─────────────────────
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.message === 'Request canceled') {
      return
    }

    const router = getRouter()

    try {
      const status = error.response?.status
      const url = error.config?.url || 'Unknown'
      const isStripeError = url.includes('stripe')

      // Log détaillé pour les erreurs Stripe
      if (isStripeError) {
        console.error('🔴 STRIPE ERROR (FRONTEND):', {
          status,
          url,
          method: error.config?.method?.toUpperCase(),
          error: error.response?.data?.error,
          code: error.response?.data?.code,
          timestamp: new Date().toISOString(),
        })
      }

      if (status === 401 || status === 403) {
        if (error.response.data.error === 'Une erreur s\'est produite lors de la tentative de vérification du jeton d\'accès utilisateur 01Cell') {
          if (process.env.VUE_APP_AUTHENTIFICATION === 'oidc') return store.dispatch('oidcStore/signOutOidc')
        } else {
          router.push({ name: 'unauthorized' })
        }
      } else if (status === 400) {
        notify.error(error.response.data.error)
      } else if (status === 404) {
        const message = errorLanguage['The request does not exist'][localStorage.getItem('APP_LANG_KEY')]
        notify.error(message)
      } else if (status === 500) {
        router.push({ name: 'error500' })
      }

      if (error.response) sendEmailError(error.response)
    } catch (catchError) {
      logger.error('Interceptor error', catchError)
      router.push({ name: 'error500' })
    }

    return Promise.reject(error.response)
  },
)

// ──────────────────────────────────────────────
// Error email notification
// ──────────────────────────────────────────────

const sendEmailError = async (error) => {
  try {
    const authorization = await getAuthorization()

    // Préparer les informations d'erreur détaillées
    const errorDetails = {
      timestamp: new Date().toLocaleString('en-US', { timeZone: 'America/Montreal' }),
      user: store.state.userInfo?.info?.mail || 'Unknown',
      endpoint: error.config?.url || 'Unknown',
      method: error.config?.method?.toUpperCase() || 'Unknown',
      statusCode: error.status || error.response?.status || 'Unknown',
      statusText: error.statusText || error.response?.statusText || 'Unknown',
      errorMessage: error.data?.error || error.message || 'Unknown error',
      environment: process.env.NODE_ENV,

      // Détails Stripe si applicable
      ...(error.config?.url?.includes('stripe') && {
        stripeError: error.data?.error,
        stripeCode: error.data?.code,
        stripeDetails: error.data?.details,
      }),

      // Informations de la requête
      request: {
        url: error.config?.url,
        method: error.config?.method,
        headers: {
          'Content-Type': error.config?.headers?.['Content-Type'],
          'Accept-Language': error.config?.headers?.['Content-Language'],
        },
        // Ne pas envoyer les données sensibles
        data: error.config?.url && SENSITIVE_ROUTES.some((route) => error.config.url.includes(route))
          ? '*** SENSITIVE DATA - CREDIT CARD ***'
          : error.config?.data,
      },

      // Informations de la réponse
      response: {
        status: error.status || error.response?.status,
        headers: error.response?.headers,
        data: error.data || error.response?.data,
      },

      // Contexte utilisateur
      userInfo: {
        id: store.state.userInfo?.clientInfo?.id,
        name: store.state.userInfo?.clientInfo?.nom,
        email: store.state.userInfo?.clientInfo?.email,
        roles: store.state.userInfo?.roles,
      },
    }

    console.error('🔴 FRONTEND ERROR LOGGED:', errorDetails)

    const to = ERROR_EMAILS
    if (to.length === 0) {
      logger.warn('No error email recipients configured')
      return
    }

    const mailOptions = {
      to,
      subject: `[${process.env.NODE_ENV}] Erreur Frontend - ${errorDetails.statusCode} ${errorDetails.method} ${errorDetails.endpoint}`,
      html: `
        <h2>Erreur Frontend détectée</h2>
        <p><strong>Environnement:</strong> ${errorDetails.environment}</p>
        <p><strong>Utilisateur:</strong> ${errorDetails.user}</p>
        <p><strong>Heure:</strong> ${errorDetails.timestamp}</p>
        <p><strong>Endpoint:</strong> ${errorDetails.method} ${errorDetails.endpoint}</p>
        <p><strong>Status:</strong> ${errorDetails.statusCode} ${errorDetails.statusText}</p>
        <p><strong>Message:</strong> ${errorDetails.errorMessage}</p>
      `,
      attachments: [
        {
          filename: 'error-details.json',
          content: JSON.stringify(errorDetails, null, 2),
        },
      ],
    }

    axios.post(
      `${process.env.VUE_APP_SERVER_ADDRESS}${process.env.VUE_APP_SERVER_ADDRESS_PATH}/mailer`,
      mailOptions,
      {
        headers: {
          Authorization: authorization,
          'Content-Language': localStorage.getItem('APP_LANG_KEY'),
        },
      },
    )
  } catch (err) {
    logger.error('sendEmailError failed', err)
  }
}

export default apiClient
